import React, { useEffect, useState } from 'react';
import { BpDesignSystem } from '../../types';
import { Box } from '../Box/Box';
import { FileCard } from '../FileCard/FileCard';
import { UploadButton, UploadButtonAccept } from '../UploadButton';

export type DocumentFieldProps = BpDesignSystem.Props<{
  buttonText?: string;
  canEdit?: boolean;
  dataTest?: string;
  disabled?: boolean;
  error?: string | null;
  fileName?: string;
  fileSize?: number;
  showCompleted?: boolean;
  onDelete?: () => void;
  onDownload?: () => void;
  onUpload?: (file: File) => Promise<void>;
  onUploadAbort?: () => void;
  progress?: number;
  uploading?: boolean;
  /**
   * @deprecated Use `accept` prop instead to specify accepted file types for upload.
   */
  whiteList?: string | undefined;
  accept?: UploadButtonAccept[];
}>;

const fileWhiteList: UploadButtonAccept[] = [
  '.xlsx',
  '.xls',
  '.csv',
  '.doc',
  '.txt',
  '.pdf',
  '.png',
  '.jpg',
  '.gif',
  '.docx'
];

export const DocumentField: React.FC<DocumentFieldProps> = ({
  buttonText,
  canEdit = true,
  disabled,
  fileName,
  error,
  fileSize,
  showCompleted,
  onDelete,
  onDownload,
  onUpload,
  onUploadAbort,
  progress,
  uploading,
  accept,
  whiteList,
  ...rest
}) => {
  const isEmpty = !fileName;
  const [fileNameState, setFileNameState] = useState(fileName);
  const [localFileSize, setLocalFileSize] = useState<number | undefined>(undefined);

  useEffect(() => {
    setFileNameState(fileName);
    if (!fileName) {
      setLocalFileSize(undefined);
    }
  }, [fileName]);

  const localUpload = async (file: File) => {
    if (file) {
      setFileNameState(file.name);
      setLocalFileSize(undefined);
      await onUpload?.(file);
      setFileNameState(undefined);
      setLocalFileSize(file.size);
    }
  };

  const showFileCard = !isEmpty || fileNameState || (!isEmpty && fileSize);

  return (
    <Box {...rest}>
      {!!showFileCard ? (
        <FileCard
          dataTest={`download-${fileName || fileNameState}`}
          uploading={progress || uploading}
          file={{
            name: fileName || fileNameState,
            size: localFileSize === undefined && !showCompleted ? fileSize : undefined
          }}
          completed={localFileSize ?? (showCompleted ? fileSize : undefined)}
          onDownload={onDownload}
          readonly={disabled}
          onDelete={canEdit ? onDelete : undefined}
          onUploadAbort={canEdit ? onUploadAbort : undefined}
        />
      ) : canEdit ? (
        <UploadButton
          whiteList={whiteList}
          accept={fileWhiteList}
          dataTest="document-field-upload-button"
          disabled={disabled}
          onFileSelect={(file) => localUpload(file)}
        />
      ) : null}
    </Box>
  );
};
